import React from 'react';
import { motion } from 'framer-motion';
import { Leaf, Recycle, Droplets } from 'lucide-react';

const SustainabilitySection = () => {
  const initiatives = [
    {
      icon: Leaf,
      title: 'Responsible Products',
      description: 'Environmentally certified cleaning products that meet commercial performance standards.',
    },
    {
      icon: Recycle,
      title: 'Waste Reduction',
      description: 'Structured waste minimisation and resource efficiency protocols across operations.',
    },
    {
      icon: Droplets,
      title: 'Sustainable Operations',
      description: 'Documented environmental practices integrated into our service delivery framework.',
    },
  ];

  return (
    <section
      id="sustainability"
      className="section-padding bg-slate-50"
      data-testid="sustainability-section"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Content */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <span className="font-manrope text-sm font-semibold text-green-600 uppercase tracking-widest mb-4 block">
              Environmental Responsibility
            </span>
            <h2
              className="font-oswald text-3xl md:text-4xl lg:text-5xl font-bold text-slate-800 uppercase tracking-tight mb-6"
              data-testid="sustainability-headline"
            >
              Sustainable{' '}
              <span className="text-green-600">Service Delivery</span>
            </h2>
            <p className="font-manrope text-lg text-slate-600 mb-8">
              Environmental responsibility is integrated into our operational framework. We deliver 
              effective commercial cleaning using certified eco-friendly products and sustainable 
              practices — supporting your organisation's environmental commitments without compromising results.
            </p>

            {/* Initiatives */}
            <div className="space-y-6">
              {initiatives.map((initiative, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: index * 0.1 }}
                  className="flex items-start gap-4"
                  data-testid={`sustainability-initiative-${index}`}
                >
                  <div className="w-12 h-12 bg-green-100 rounded-sm flex items-center justify-center flex-shrink-0">
                    <initiative.icon className="w-6 h-6 text-green-600" />
                  </div>
                  <div>
                    <h3 className="font-oswald text-lg font-bold text-slate-800 uppercase mb-1">
                      {initiative.title}
                    </h3>
                    <p className="font-manrope text-sm text-slate-600">
                      {initiative.description}
                    </p>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Visual */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="relative"
          >
            <div className="img-container rounded-sm overflow-hidden shadow-2xl">
              <img
                src="https://images.unsplash.com/photo-1683559086021-7f5e3b5e11cb?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2Mzl8MHwxfHNlYXJjaHwxfHxwcm9mZXNzaW9uYWwlMjBjbGVhbmluZyUyMHBlcnNvbiUyMHNwcmF5JTIwYm90dGxlJTIwd2lwaW5nJTIwc3VyZmFjZXxlbnwwfHx8fDE3NzM4OTk2MTV8MA&ixlib=rb-4.1.0&q=85"
                alt="Eco-friendly commercial cleaning spray bottle - sustainable cleaning services London"
                className="w-full h-[400px] object-cover"
              />
            </div>
            {/* Eco badge */}
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.5 }}
              className="absolute -bottom-6 -right-6 bg-green-600 p-6 rounded-sm shadow-xl"
            >
              <Leaf className="w-8 h-8 text-white mb-2" />
              <div className="font-oswald text-sm font-bold text-white uppercase">
                Eco Focused
              </div>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default SustainabilitySection;
